import { useEffect, useRef, useState } from 'react';

/**
 * A face animated from its approved master drawing.
 *
 * The face is a cut-out puppet, not a video. The master was cut into a body
 * and a head, and the avatar tool drew the few extra pictures the puppet needs
 * as edits of that master: a blink, three glances, the win and loss faces and
 * a head turned to the front. Every layer is a full-frame image on the
 * master's canvas, so the layers stack with no offsets and a pivot is a
 * percentage of the frame (`ui/css/avatar.css`).
 *
 * The head sits under the body, so the collar hides the bottom of the neck.
 * Each motion owns one wrapper, so no two animations fight over a transform:
 * the mirror turns the whole bust to face right, breathing lifts the head,
 * the sway rocks the bust, a glance tilts it, a reaction plays and
 * holds, and a turn squashes across the swap. The mirror takes the body too:
 * a head flipped alone left its neck off the collar's opening. It sits outside
 * the reaction, so a right-facing face still throws its head back on a win.
 *
 * A self-locked edit redraws every line of a face a pixel or two off
 * (`docs/dev/avatars.md`), so drawings swap on a hard cut, never a crossfade.
 * The small patches (blink, glances) cover only the eyes, where the seam
 * falls in flat skin; the big changes swap the whole head.
 *
 * The house bots wear it in the game (`ui/botFaces.ts`). The Shared - Avatars
 * sketch auditions the whole cast on it, Scout as the reference.
 */

export type Mood = 'rest' | 'win' | 'loss';
export type Facing = 'left' | 'front' | 'right';
/** Where the eyes go. `idle` schedules automatic glances. */
export type Gaze = 'idle' | 'you' | 'side' | 'down' | 'up' | 'blink';
/**
 * `playful` auditions BRAND.md open question 4 (a squash, an overshoot, a
 * laugh); `settle` keeps the brand's rule and only lands.
 */
export type Motion = 'playful' | 'settle';
/** Which slice of the breath and the sway a copy starts on, so copies never move in step. */
export type Phase = 0 | 1 | 2 | 3;

type Drawing = 'neutral' | 'win' | 'loss' | 'front';
type Patch = 'blink' | 'side' | 'down' | 'up';
type Side = 'left' | 'right';

export interface RigConfig {
  /** The face's cast slug, which names it in the avatar tools and the sketch. */
  readonly slug: string;
  /** Where its layers are served from, a path on the client's origin. */
  readonly dir: string;
  /** A continuous bust: no neck joint, so the bust moves from its bottom edge. */
  readonly wholeBust?: boolean;
  /** A class that sets the head's pivot at its neck. A whole bust needs none. */
  readonly pivotClass?: string;
  /**
   * A class for the loss drawing, for a retake that needs a measured fit to
   * land on the rest drawing (the sketch's cast). The house bots need none.
   */
  readonly lossClass?: string;
  /** Drawings whose jaw sits over the collar, cut as a layer above the body. */
  readonly chinDrawings: readonly Drawing[];
}

const DRAWINGS: readonly Drawing[] = ['neutral', 'win', 'loss', 'front'];
const PATCHES: readonly Patch[] = ['blink', 'side', 'down', 'up'];
const PATCH_FILE: Record<Patch, string> = {
  blink: 'blink',
  side: 'left',
  down: 'down',
  up: 'up',
};

/** Every file one rig draws. The render and `rigLayers` both read it. */
interface RigSources {
  readonly body: string;
  readonly head: Readonly<Record<Drawing, string>>;
  readonly eyes: Readonly<Record<Patch, string>>;
  readonly chin: Readonly<Partial<Record<Drawing, string>>>;
}

function keyed<K extends string>(keys: readonly K[], path: (key: K) => string): Record<K, string> {
  return Object.fromEntries(keys.map((key) => [key, path(key)])) as Record<K, string>;
}

function rigSources(rig: RigConfig): RigSources {
  const file = (name: string): string => `${rig.dir}/${name}.webp`;
  return {
    body: file('body'),
    head: keyed(DRAWINGS, (drawing) => file(`head-${drawing}`)),
    eyes: keyed(PATCHES, (patch) => file(`eyes-${PATCH_FILE[patch]}`)),
    chin: keyed(rig.chinDrawings, (drawing) => file(`chin-${drawing}`)),
  };
}

/**
 * Every file a rig draws, for a test to check that each one ships. The rig
 * names its files in code, not from the disk, so a layer that is renamed or
 * never exported is a hole in the puppet rather than a 404 in a log.
 */
export function rigLayers(rig: RigConfig): readonly string[] {
  const { body, head, eyes, chin } = rigSources(rig);
  return [body, ...Object.values(head), ...Object.values(eyes), ...Object.values(chin)];
}

/** A full-frame layer: every layer stacks on the master's canvas with no offset. */
export const RIG_LAYER = 'absolute inset-0 block size-full select-none';

/**
 * One full-frame layer of the puppet. A layer not drawn right now stays
 * mounted and invisible, so a swap is a hard cut with the image already loaded.
 */
function Layer({
  src,
  shown = true,
  className = '',
}: {
  readonly src: string;
  readonly shown?: boolean;
  readonly className?: string;
}): React.JSX.Element {
  return (
    <img
      className={`${RIG_LAYER} ${className} ${shown ? '' : 'invisible'}`}
      src={src}
      alt=""
      draggable={false}
    />
  );
}

/** What a mood change plays: the new mood, or the settle back from the old one. */
type Reaction = '' | 'win' | 'loss' | 'rest-from-win' | 'rest-from-loss';

/** Complete literals, so Tailwind and the style scan see every class. */
const HEAD_REACT: Record<Motion, Record<Reaction, string>> = {
  playful: {
    '': '',
    win: 'rig-react-win',
    loss: 'rig-react-loss',
    'rest-from-win': 'rig-react-rest-from-win',
    'rest-from-loss': 'rig-react-rest-from-loss',
  },
  settle: {
    '': '',
    win: 'rig-react-win-settle',
    loss: 'rig-react-loss',
    'rest-from-win': 'rig-react-rest-from-win',
    'rest-from-loss': 'rig-react-rest-from-loss',
  },
};

const BODY_REACT: Record<Motion, Record<Reaction, string>> = {
  playful: {
    '': '',
    win: 'rig-body-win',
    loss: 'rig-body-loss',
    'rest-from-win': '',
    'rest-from-loss': 'rig-body-rest-from-loss',
  },
  settle: {
    '': '',
    win: '',
    loss: 'rig-body-loss',
    'rest-from-win': '',
    'rest-from-loss': 'rig-body-rest-from-loss',
  },
};

const LOOK: Record<Patch | 'none', string> = {
  none: '',
  blink: '',
  side: 'rig-look-side',
  down: 'rig-look-down',
  up: 'rig-look-up',
};

const PHASE: Record<Phase, string> = {
  0: '',
  1: 'rig-phase-1',
  2: 'rig-phase-2',
  3: 'rig-phase-3',
};

/**
 * When the new face lands, in milliseconds after the mood changes. A playful
 * win crouches on the old face and swaps at the top of the pop (16 percent of
 * 720); a loss takes its breath first (28 percent of 1300).
 */
const FACE_DELAY: Record<Motion, Record<Mood, number>> = {
  playful: { rest: 0, win: 115, loss: 365 },
  settle: { rest: 0, win: 0, loss: 365 },
};

/** The blink drawing squeezes, so a long one reads as a wince. */
const BLINK_MS = 80;

/**
 * How long the eyes stay shut as a result face returns to rest: long enough
 * to cover the swap, well short of the held blink that reads as a wince.
 */
export const SETTLE_BLINK_MS = 150;

export function between(low: number, high: number): number {
  return low + Math.random() * (high - low);
}

/** A run of timers that all die with the effect that set them. */
export function timerBag(): {
  readonly later: (ms: number, work: () => void) => void;
  readonly clear: () => void;
} {
  const timers: ReturnType<typeof setTimeout>[] = [];
  return {
    later: (ms, work) => {
      timers.push(setTimeout(work, ms));
    },
    clear: () => {
      for (const timer of timers) clearTimeout(timer);
    },
  };
}

/**
 * The idle eyes: a blink every few seconds (sometimes a double), and, when
 * `glances` is on, a look across the table, down at the hand or up to think
 * that comes back to you, often with a blink on the way.
 */
function useIdleEyes(
  enabled: boolean,
  glances: boolean,
): { blinking: boolean; glance: Patch | undefined } {
  const [blinking, setBlinking] = useState(false);
  const [glance, setGlance] = useState<Patch | undefined>(undefined);
  useEffect(() => {
    if (!enabled) return undefined;
    const { later, clear } = timerBag();
    const blink = (then?: () => void): void => {
      setBlinking(true);
      later(BLINK_MS, () => {
        setBlinking(false);
        then?.();
      });
    };
    const scheduleBlink = (): void => {
      later(between(2200, 5600), () => {
        blink(() => {
          if (Math.random() < 0.2) {
            later(140, () => {
              blink(scheduleBlink);
            });
          } else {
            scheduleBlink();
          }
        });
      });
    };
    const scheduleGlance = (): void => {
      later(between(3800, 8200), () => {
        const roll = Math.random();
        setGlance(roll < 0.5 ? 'side' : roll < 0.85 ? 'down' : 'up');
        later(between(900, 1900), () => {
          setGlance(undefined);
          if (Math.random() < 0.3) blink();
          scheduleGlance();
        });
      });
    };
    scheduleBlink();
    if (glances) scheduleGlance();
    return () => {
      clear();
      setBlinking(false);
      setGlance(undefined);
    };
  }, [enabled, glances]);
  return { blinking, glance };
}

/**
 * The facing on screen, which trails the facing asked for through a turn: a
 * squash, and the swap inside it. Left to right passes through the front
 * drawing, so the head never flips in one frame. The mirror remembers the
 * last side, so a right seat turning to the front keeps its body where it
 * is; a crossing flips the body on the cut into the front, which is nearly
 * symmetric.
 */
function useTurn(facing: Facing, still: boolean): { shown: Facing; side: Side; turning: boolean } {
  const [shown, setShown] = useState(facing);
  const [side, setSide] = useState<Side>(facing === 'right' ? 'right' : 'left');
  const [turning, setTurning] = useState(false);
  const from = useRef(facing);
  useEffect(() => {
    const start = from.current;
    from.current = facing;
    if (start === facing) return undefined;
    const next: Side | undefined = facing === 'front' ? undefined : facing;
    if (still) {
      setShown(facing);
      if (next !== undefined) setSide(next);
      return undefined;
    }
    const crossing = start !== 'front' && facing !== 'front';
    const { later, clear } = timerBag();
    setTurning(true);
    later(80, () => {
      setShown(crossing ? 'front' : facing);
      if (next !== undefined) setSide(next);
    });
    if (crossing) {
      later(150, () => {
        setShown(facing);
      });
    }
    later(290, () => {
      setTurning(false);
    });
    return () => {
      clear();
      setShown(facing);
      if (next !== undefined) setSide(next);
      setTurning(false);
    };
  }, [facing, still]);
  return { shown, side, turning };
}

/** The reaction a mood change plays, and the mood whose face is showing, which trails it. */
function useReaction(
  mood: Mood,
  replay: number,
  motion: Motion,
  still: boolean,
): { reaction: Reaction; face: Mood } {
  const previous = useRef<Mood>(mood);
  const [reaction, setReaction] = useState<Reaction>('');
  const [face, setFace] = useState<Mood>(mood);
  useEffect(() => {
    const was = previous.current;
    previous.current = mood;
    if (mood !== 'rest') setReaction(mood);
    else if (was !== 'rest') setReaction(was === 'win' ? 'rest-from-win' : 'rest-from-loss');
    const delay = still ? 0 : FACE_DELAY[motion][mood];
    if (delay === 0) {
      setFace(mood);
      return undefined;
    }
    setFace(was === mood ? 'rest' : was);
    const timer = setTimeout(() => {
      setFace(mood);
    }, delay);
    return () => {
      clearTimeout(timer);
      setFace(mood);
    };
  }, [mood, replay, motion, still]);
  return { reaction, face };
}

/**
 * Eyes shut as a win or loss returns to rest, so the swap back to the rest
 * head lands inside a blink instead of on a cut.
 */
function useSettleBlink(mood: Mood, still: boolean): boolean {
  const previous = useRef<Mood>(mood);
  const [shut, setShut] = useState(false);
  useEffect(() => {
    const was = previous.current;
    previous.current = mood;
    if (still || mood !== 'rest' || was === 'rest') return undefined;
    setShut(true);
    const timer = setTimeout(() => {
      setShut(false);
    }, SETTLE_BLINK_MS);
    return () => {
      clearTimeout(timer);
      setShut(false);
    };
  }, [mood, still]);
  return shut;
}

/**
 * What the rig tells a rider: a second puppet drawn over the body, inside the
 * head's motion, that follows the face. The sketch's shoulder parrot is one.
 *
 * The rider draws itself through `wrap`, the head's motion wrappers. A
 * reaction restarts those by remounting them, so a rider that kept its state
 * inside them would lose it on every win and loss. The rider's element sits
 * outside them instead, and its hooks live across a reaction.
 */
export interface RigRider {
  /** A held blink or a glance, while the face is free to look. Undefined is at you. */
  readonly look: Patch | undefined;
  readonly mood: Mood;
  /** The facing on screen, which trails the facing asked for through a turn. */
  readonly shown: Facing;
  readonly motion: Motion;
  readonly still: boolean;
  readonly replay: number;
  /** The reaction playing now; empty when still. */
  readonly reaction: Reaction;
  /** Changes with every reaction and replay: a key that restarts one. */
  readonly beat: string;
  /** Wraps the rider's drawing in the head's motion, so it rides the bust. */
  readonly wrap: (content: React.ReactNode) => React.JSX.Element;
}

export interface AvatarRigProps {
  readonly rig: RigConfig;
  readonly mood: Mood;
  readonly facing: Facing;
  readonly gaze: Gaze;
  /**
   * Box, corner, plate, border and ring: the frame the head is drawn in. A
   * caller that draws its own frame around the rig passes `size-full`.
   */
  readonly frame: string;
  /** Settle unless the caller auditions Playful (BRAND.md: interactions settle). */
  readonly motion?: Motion;
  readonly phase?: Phase;
  /** No motion at all: the 20 chip, the frame strip and reduced motion. */
  readonly still?: boolean;
  /** Hold the eyes shut, for the frame strip. */
  readonly blink?: boolean;
  /** Bumped to play the current mood's reaction again. */
  readonly replay?: number;
  /** A second puppet on the bust, drawn from the rig's state. */
  readonly rider?: (state: RigRider) => React.ReactNode;
}

export function AvatarRig({
  rig,
  mood,
  facing,
  gaze,
  frame,
  motion = 'settle',
  phase = 0,
  still = false,
  blink = false,
  replay = 0,
  rider,
}: AvatarRigProps): React.JSX.Element {
  const sources = rigSources(rig);
  const { shown, side, turning } = useTurn(facing, still);
  const { reaction, face } = useReaction(mood, replay, motion, still);
  const settling = useSettleBlink(mood, still);
  const drawing: Drawing = shown === 'front' ? 'front' : face === 'rest' ? 'neutral' : face;
  // The patches are cut from the three-quarter rest head, so only it blinks and
  // glances, and only at rest: a reaction starts from eyes on you, not from a
  // glance left over in the crouch.
  const patchable = drawing === 'neutral';
  const free = patchable && mood === 'rest';
  const idle = useIdleEyes(!still && free, gaze === 'idle');
  const look = !free
    ? undefined
    : gaze === 'idle'
      ? idle.glance
      : gaze === 'you'
        ? undefined
        : gaze;
  const shut = patchable && (idle.blinking || blink || settling);
  const patch = !patchable ? undefined : shut ? 'blink' : look;
  const played = still ? '' : reaction;
  const beat = `${reaction}-${String(replay)}`;
  const tilt = still || !free ? 'none' : (look ?? 'none');
  const squash = !still && turning && motion === 'playful' ? 'rig-turn' : '';

  const layer = 'absolute inset-0';
  // A continuous bust has no neck joint, so its breath and sway are anchored at
  // the base. The utility outranks any pivot class, whatever the stylesheet order.
  const pivot = rig.wholeBust ? 'rig-pivot origin-bottom' : 'rig-pivot';
  // The head's motion, one wrapper per move. Drawn twice with the same classes,
  // so the head under the body and its chin over it move as one.
  const headChain = (content: React.ReactNode): React.JSX.Element => (
    <div className={`${layer} ${pivot} ${still ? '' : 'rig-breathe-head'}`}>
      <div className={`${layer} ${pivot} ${still ? '' : 'rig-sway'}`}>
        <div className={`${layer} ${pivot} rig-look ${LOOK[tilt]}`}>
          <div key={beat} className={`${layer} ${pivot} ${HEAD_REACT[motion][played]}`}>
            <div className={`${layer} ${pivot} ${squash}`}>{content}</div>
          </div>
        </div>
      </div>
    </div>
  );
  return (
    <div
      className={`relative flex-none overflow-hidden ${frame} ${PHASE[phase]} ${rig.pivotClass ?? ''} ${rig.wholeBust ? 'rig-whole' : ''}`}
    >
      <div className={`${layer} ${side === 'right' ? '-scale-x-100' : ''}`}>
        {headChain(
          <>
            {DRAWINGS.map((name) => (
              <Layer
                key={name}
                src={sources.head[name]}
                shown={name === drawing}
                className={name === 'loss' ? (rig.lossClass ?? '') : ''}
              />
            ))}
            {PATCHES.map((name) => (
              <Layer key={name} src={sources.eyes[name]} shown={name === patch} />
            ))}
          </>,
        )}
        <div className={`${layer} origin-bottom ${still ? '' : 'rig-breathe-body'}`}>
          <Layer
            key={beat}
            src={sources.body}
            className={`origin-bottom ${BODY_REACT[motion][played]}`}
          />
        </div>
        {headChain(
          DRAWINGS.map((name) => {
            const src = sources.chin[name];
            return src === undefined ? null : (
              <Layer
                key={name}
                src={src}
                shown={name === drawing}
                className={name === 'loss' ? (rig.lossClass ?? '') : ''}
              />
            );
          }),
        )}
        {rider?.({
          look,
          mood,
          shown,
          motion,
          still,
          replay,
          reaction: played,
          beat,
          wrap: headChain,
        })}
      </div>
    </div>
  );
}
